import { defineStore } from 'pinia';
import { computed, shallowRef } from 'vue';

import LanguageSetting from './LanguageSetting.vue';
import ThemeSetting from './ThemeSetting.vue';
import { resolveValue } from './resolveValue.js';
import { useShellWords } from './shellWords.js';

// The Settings store: what the Settings page shows.
//
// It works the way the App Menu store does. Two settings are here from the
// start — Theme and Language — and an app or a feature registers its own beside
// them, or removes one it does not want.
//
// A setting is three things: the name of the group it sits in, its own name,
// and the component that is its control. The store does not know what a setting
// changes; the component does.
//
// A group is its name. Two settings that give the same group name sit together,
// and a group appears where its first setting was registered.

function assertUsableSetting(setting, registered) {
    if (!setting || typeof setting !== 'object') {
        throw new Error('SETTING_INVALID');
    }
    if (typeof setting.id !== 'string' || setting.id.length === 0) {
        throw new Error('SETTING_ID_REQUIRED');
    }
    if (registered.some((existing) => existing.id === setting.id)) {
        // Two features claiming one id would silently shadow each other, and
        // which one won would depend on import order.
        throw new Error('SETTING_DUPLICATE');
    }
    if (setting.group === undefined || setting.group === null) {
        throw new Error('SETTING_GROUP_REQUIRED');
    }
    if (setting.name === undefined || setting.name === null) {
        throw new Error('SETTING_NAME_REQUIRED');
    }
    if (!setting.component) {
        throw new Error('SETTING_COMPONENT_REQUIRED');
    }
}

const useSettingsStore = defineStore('shellSettings', function () {
    const word = useShellWords();

    // The two settings every app carries. They are settings like any other, so
    // an app removes one with `removeItem`. A name is a getter, so it follows a
    // language change.
    const standardSettings = [
        { id: 'theme', group: () => word('shell.settings.appearance'), name: () => word('shell.settings.theme'), component: ThemeSetting },
        { id: 'language', group: () => word('shell.settings.language'), name: () => word('shell.settings.appLanguage'), component: LanguageSetting }
    ];

    // A shallow ref, replaced on every change: a setting carries a component and
    // getters that must reach the page as they were handed over, not wrapped in
    // a proxy.
    const registered = shallowRef([...standardSettings]);

    function register(...settings) {
        for (const setting of settings.flat()) {
            assertUsableSetting(setting, registered.value);
            registered.value = [...registered.value, setting];
        }
    }

    // Take a setting out — one an app added, or a standard one. An id nobody
    // registered is refused rather than ignored, so a mistyped id is loud.
    function removeItem(id) {
        if (!registered.value.some((setting) => setting.id === id)) {
            throw new Error('SETTING_NOT_REGISTERED');
        }
        registered.value = registered.value.filter((setting) => setting.id !== id);
    }

    // The page renders THIS, never the raw registrations: names are resolved
    // here, so a template never calls a function it was handed.
    const settings = computed(() => registered.value.map((setting) => ({
        id: setting.id,
        group: resolveValue(setting.group),
        name: resolveValue(setting.name),
        component: setting.component,
        order: setting.order ?? 0
    })));

    // Grouped, ready to render. Groups keep the order their first setting was
    // registered in; inside a group, `order`, then registration order.
    const groups = computed(function () {
        const bucketed = new Map();
        for (const setting of settings.value) {
            if (!bucketed.has(setting.group)) {
                bucketed.set(setting.group, []);
            }
            bucketed.get(setting.group).push(setting);
        }
        return [...bucketed.entries()].map(([name, groupSettings]) => ({
            name,
            settings: [...groupSettings].sort((left, right) => left.order - right.order)
        }));
    });

    return { registered, settings, groups, register, removeItem };
});

export { useSettingsStore };
